import { css, StyleSheet } from 'aphrodite'
import { Amplify, Auth } from 'aws-amplify'
import parsePhoneNumber from 'libphonenumber-js'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import ReactCodeInput from 'react-code-input'
import ReactGa from 'react-ga4'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import Geocode from 'react-geocode'
import LayoutModal from '../../components/Layout/LayoutModal'
import UIButton from '../../components/UI/UIButton'
import UICheckbox from '../../components/UI/UICheckbox'
import UIInput from '../../components/UI/UIInput'
import UILink from '../../components/UI/UILink'
import UIPassword from '../../components/UI/UIPassword'
import config from '../../config/aws'
import { leToBr } from '../../utils/Utils'
import GooglePlacesAutocomplete from 'react-google-places-autocomplete'
import { COLOR_ERROR, COLOR_GRAY__300, COLOR_GRAY__600 } from '../../lib/colors'
import { X } from 'react-feather'

Amplify.configure({
  Auth: config.aws,
})

const styles = StyleSheet.create({
  inputInline: {
    margin: 0,
    padding: 0,
    display: 'flex',
  },
  input: {
    width: '100%',
  },
  checkContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
    margin: '1.5em 0em',
  },
  check: {
    color: 'black',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  modal: {
    display: 'flex',
    justifyContent: 'center',
  },
  labelCode: {
    color: 'rgb(113, 128, 150)',
    padding: '5px 0 5px 0',
    fontWeight: 400,
    fontSize: '14px',
  },
  error: {
    fontWeight: 400,
    fontSize: 11,
    color: COLOR_ERROR,
  },
  addressInput: {
    position: 'relative',
  },
  resetAddress: {
    position: 'absolute',
    background: 'transparent',
    border: 'none',
    right: 0,
    top: 0,
    bottom: 0,
    cursor: 'pointer',
  },
})

const customStyles = (error) => {
  return {
    control: (provided) => ({
      ...provided,
      borderColor: error ? COLOR_ERROR : COLOR_GRAY__300,
      fontSize: 15,
    }),
    valueContainer: (provided) => ({
      ...provided,
      display: 'flex',
      padding: '10px 16px',
      alignItems: 'center',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      display: 'none',
    }),
  }
}

const RegisterWatcher = ({ isOpened, onClose, onReturn, onSubscription }) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({ mode: 'all' })

  const { t } = useTranslation('translation')

  const [Validate, setValidate] = useState(false)
  const [Mail, setMail] = useState('')
  const [enterpriseIsRequired, setEnterpriseIsRequired] = useState(false)
  const [newsletterChecked, setNewsletterChecked] = useState(false)
  const [generalConditionChecked, setGeneralConditionChecked] = useState(false)
  const [privacyPoliciesChecked, setPrivacyPoliciesChecked] = useState(false)
  const [chartChecked, setChartChecked] = useState(false)
  const [isLoadingVerification, setLoadingVerification] = useState(false)
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [addressEmptyError, setAddressEmptyError] = useState(false)
  const [fullAddress, setFullAddress] = useState('')

  useEffect(() => {
    ReactGa.send(window.location.pathname + '/register-viewer')
  }, [])

  const onSubmit = async ({
    email,
    password,
    firstName,
    lastName,
    pseudo,
    phoneNumber,
    company,
    tva,
    siret,
  }) => {
    try {
      const pn = parsePhoneNumber(phoneNumber, 'FR')

      await toast.promise(
        enterpriseIsRequired
          ? Auth.signUp({
              username: email,
              password: password,
              attributes: {
                email,
                profile: JSON.stringify({
                  role: 'ubiqeyes_watcher',
                  phone_number: pn.number,
                  firstname: firstName,
                  lastname: lastName,
                  nickname: pseudo,
                  company: {
                    name: company,
                    tva: tva,
                    siret: siret,
                  },
                  formatted_address: fullAddress.label,
                  city_name: city || '',
                  country_name: country || '',
                  postcode: zipCode || '',
                  address_line1: address || '',
                  consent_newsletter: newsletterChecked,
                }),
              },
            })
          : Auth.signUp({
              username: email,
              password: password,
              attributes: {
                email,
                profile: JSON.stringify({
                  role: 'ubiqeyes_watcher',
                  phone_number: pn.number,
                  firstname: firstName,
                  lastname: lastName,
                  nickname: pseudo,
                  formatted_address: fullAddress.label,
                  city_name: city || '',
                  country_name: country || '',
                  postcode: zipCode || '',
                  address_line1: address || '',
                  consent_newsletter: newsletterChecked,
                }),
              },
            }),
        {
          pending: t('pages.registerWatcher.pendingMessage'),
          success: t('pages.registerWatcher.successMessage'),
          error: t('pages.registerWatcher.errorMessage'),
        }
      )
      setMail(email)
      setValidate(true)
      reset()
      toast.info(
        t('pages.registerWatcher.emailConfirmationMessage', { email: email }),
        {
          autoClose: 10000,
          closeOnClick: true,
        }
      )
    } catch (error) {
      if (
        error.message ===
        ('An account with the given email already exists.' ||
          'PreSignUp failed with error Already registered user.')
      ) {
        toast.error(t('common.form.errorMessage.userAlreadyExist'))
      }
    }
  }

  const [valid, setValid] = useState(true)
  const submitMailVerification = async (e) => {
    if (e.length == 6) {
      try {
        setLoadingVerification(true)
        await toast.promise(Auth.confirmSignUp(Mail, e), {
          success: t('pages.confirm.success'),
          pending: t('pages.confirm.pending'),
          error: t('pages.confirm.error'),
        })
        setValid(true)
        setValidate(false)
        if (onSubscription) {
          onSubscription()
        }
      } catch (error) {
        setValid(false)
        console.log(error)
      }
    }
  }

  const parseAddress = async (location) => {
    Geocode.fromAddress(location.value.description).then(
      (response) => {
        console.log(response.results[0])
        setAddress(response.results[0].formatted_address.split(',')[0])
        setCity(
          response.results[0].address_components.find((component) =>
            component.types.includes('locality')
          )?.long_name ||
            response.results[0].address_components.find((component) =>
              component.types.includes('sublocality')
            )?.long_name
        )
        setCountry(
          response.results[0].address_components.find((component) =>
            component.types.includes('country')
          )?.long_name
        )
        setZipCode(
          response.results[0].address_components.find((component) =>
            component.types.includes('postal_code')
          )?.long_name
        )
      },
      (error) => {
        console.log(error)
      }
    )
  }

  return (
    <LayoutModal
      isOpened={isOpened}
      hasPadding
      headerLabel={t('pages.registerWatcher.header')}
      hasReturn={true}
      onReturn={onReturn}
      disable={isLoadingVerification}
      onClose={() => {
        onClose()
        setValidate(false)
      }}
    >
      {Validate ? (
        <>
          <h3 className={css(styles.title)}>{t('pages.login.waitingMail')}</h3>
          <h4>{Mail}</h4>
          <div className={css(styles.labelCode)}>
            {t('pages.login.codeToActivate')}
          </div>
          <ReactCodeInput
            type="text"
            isValid={valid}
            onChange={submitMailVerification}
            fields={6}
            disable={isLoadingVerification}
          />
        </>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('pages.registerWatcher.firstName')}*`}
                errorMessage={errors.firstName && errors.firstName.message}
              />
            )}
            rules={{
              required: {
                value: true,
                message: t('common.form.errorMessage.firstNameEmptyError'),
              },
            }}
          />
          <Controller
            name="pseudo"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('pages.registerWatcher.pseudo')}*`}
                errorMessage={errors.pseudo && errors.pseudo.message}
              />
            )}
            rules={{
              required: {
                value: true,
                message: t('common.form.errorMessage.pseudoEmptyError'),
              },
            }}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('pages.registerWatcher.lastName')}*`}
                errorMessage={errors.lastName && errors.lastName.message}
              />
            )}
            rules={{
              required: {
                value: true,
                message: t('common.form.errorMessage.lastNameEmptyError'),
              },
            }}
          />
          <br />

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                placeholder={`${t('common.form.email')}*`}
                errorMessage={errors.email && errors.email.message}
              />
            )}
            rules={{
              required: {
                value: true,
                message: t('common.form.errorMessage.emailEmptyError'),
              },
            }}
          />

          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <UIInput
                {...field}
                type="tel"
                placeholder={`${t('pages.registerWatcher.phoneNumber')}*`}
                errorMessage={errors.phoneNumber && errors.phoneNumber.message}
              />
            )}
            rules={{
              required: {
                value: true,
                message: t('common.form.errorMessage.phoneNumberEmptyError'),
              },
            }}
          />
          <br />
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <UIPassword
                {...field}
                placeholder={`${t('common.form.password')}*`}
                errorMessage={errors.password && errors.password.message}
              />
            )}
            rules={{
              required: {
                value: true,
                message: t('common.form.errorMessage.passwordEmptyError'),
              },
              pattern: {
                value:
                  /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z\d]+).{8,}$/,
                message: t('common.form.errorMessage.passwordNotValidError'),
              },
              minLength: {
                value: '8',
                message: t('common.form.errorMessage.passwordMinLengthError'),
              },
            }}
          />
          <br />
          <div className={css(styles.addressInput)}>
            <GooglePlacesAutocomplete
              apiOptions={{ language: 'fr', region: 'fr' }}
              selectProps={{
                value: fullAddress,
                onChange: (location) => {
                  setAddressEmptyError(false)
                  setFullAddress(location)
                  parseAddress(location)
                },
                placeholder: t('pages.createRequest.enterTheFullAddress'),
                styles: customStyles(addressEmptyError),
              }}
            />
            <button
              className={css(styles.resetAddress)}
              type="button"
              onClick={() => setFullAddress('')}
            >
              <X color={COLOR_GRAY__600} />
            </button>
          </div>
          {addressEmptyError && (
            <p className={css(styles.error)}>
              {t('common.form.errorMessage.addressEmptyError')}
            </p>
          )}
          <div className={css(styles.checkContainer)}>
            <Controller
              name="enterpriseAccount"
              control={control}
              render={({ field }) => (
                <UICheckbox
                  {...field}
                  label={`${t('pages.registerWatcher.enterpriseAccount')}`}
                  onChange={(e) => setEnterpriseIsRequired(e.target.checked)}
                />
              )}
            />
            {enterpriseIsRequired && (
              <>
                <Controller
                  name="company"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      {...field}
                      placeholder={`${t(
                        'pages.registerWatcher.enterpriseName'
                      )}*`}
                      errorMessage={
                        errors.enterpriseName && errors.enterpriseName.message
                      }
                    />
                  )}
                  rules={{
                    required: {
                      value: true,
                      message: t(
                        'common.form.errorMessage.enterpriseNameEmptyError'
                      ),
                    },
                  }}
                />
                <Controller
                  name="siret"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      {...field}
                      placeholder={`${t('pages.registerWatcher.siret')}*`}
                      errorMessage={errors.siret && errors.siret.message}
                    />
                  )}
                  rules={{
                    required: {
                      value: true,
                      message: t('common.form.errorMessage.siretEmptyError'),
                    },
                    pattern: {
                      value: /^\d{14}$/,
                      message: t('common.form.errorMessage.siretError'),
                    },
                  }}
                />
                <Controller
                  name="tva"
                  control={control}
                  render={({ field }) => (
                    <UIInput
                      {...field}
                      placeholder={`${t('pages.registerWatcher.tva')}*`}
                      errorMessage={errors.tva && errors.tva.message}
                    />
                  )}
                  rules={{
                    required: {
                      value: true,
                      message: t('common.form.errorMessage.tvaEmptyError'),
                    },
                    pattern: {
                      value: /^([FR])*(\d{11})$/,
                      message: t('common.form.errorMessage.tvaError'),
                    },
                  }}
                />
              </>
            )}
            <Controller
              name="newsletter"
              control={control}
              render={({ field }) => (
                <UICheckbox
                  dynamicStyle={styles.check}
                  {...field}
                  onChange={(e) => setNewsletterChecked(e.target.checked)}
                >
                  {leToBr(t('pages.registerWatcher.optinNewsletters'))}
                </UICheckbox>
              )}
            />
            <Controller
              name="generalCondition"
              control={control}
              render={({ field }) => (
                <UICheckbox
                  dynamicStyle={styles.check}
                  {...field}
                  onChange={(e) => setGeneralConditionChecked(e.target.checked)}
                >
                  {t('pages.registerWatcher.generalCondition1')}&nbsp;
                  <UILink
                    linkStyle="custom"
                    linkTitle={t('pages.registerWatcher.generalCondition2')}
                    redirection={'/cgu.html'}
                    alt={t('pages.home.footer.cguAlt')}
                  ></UILink>
                  &nbsp;
                  {t('pages.registerWatcher.generalCondition3')}
                  <UILink
                    linkStyle="custom"
                    linkTitle={t('pages.registerWatcher.generalCondition4')}
                    redirection={'/assets/cgv.pdf'}
                    alt={t('pages.home.footer.cguAlt')}
                  ></UILink>
                  &nbsp;
                  {t('pages.registerWatcher.generalCondition5')}
                </UICheckbox>
              )}
            />
            <Controller
              name="privacyPolicies"
              control={control}
              render={({ field }) => (
                <UICheckbox
                  {...field}
                  onChange={(e) => setPrivacyPoliciesChecked(e.target.checked)}
                >
                  {t('pages.registerWatcher.privacyPolicies1')}&nbsp;
                  <UILink
                    linkStyle="custom"
                    linkTitle={t('pages.registerWatcher.privacyPolicies2')}
                    redirection={'/assets/politique-de-confidentialites.pdf'}
                    alt={t('pages.home.footer.cguAlt')}
                  ></UILink>
                  &nbsp;
                  {t('pages.registerWatcher.privacyPolicies3')}
                </UICheckbox>
              )}
            />
            <Controller
              name="chart"
              control={control}
              render={({ field }) => (
                <UICheckbox
                  {...field}
                  onChange={(e) => setChartChecked(e.target.checked)}
                >
                  {t('pages.registerWatcher.chart1')}&nbsp;
                  <UILink
                    linkStyle="custom"
                    linkTitle={t('pages.registerWatcher.chart2')}
                    redirection={'/assets/chartes-de-bienveillance.pdf'}
                    alt={t('pages.registerWatcher.chart2')}
                  ></UILink>
                </UICheckbox>
              )}
            />
          </div>
          <UIButton
            id="inscription-validee"
            type="submit"
            kind="button"
            design="primary"
            fullWidth
            disabled={
              !(
                generalConditionChecked &&
                privacyPoliciesChecked &&
                chartChecked &&
                address
              )
            }
            label={`${t('common.button.submitRegister')}`}
          />
        </form>
      )}
    </LayoutModal>
  )
}

RegisterWatcher.propTypes = {
  isOpened: PropTypes.bool,
  onClose: PropTypes.func,
  onReturn: PropTypes.func,
  onSubscription: PropTypes.func,
}

export default RegisterWatcher
